import { Labels } from '@graphistry/viz/src/containers/labels';
import { Axis } from '@graphistry/viz/src/containers/axis';
import { Logo } from '@graphistry/viz/src/containers/logo';
import { Renderer } from '@graphistry/viz/src/containers/renderer';
import { container } from '@graphistry/falcor-react-redux';
import { Selection } from '@graphistry/viz/src/containers/selection';
import { Scene as SceneComponent } from '@graphistry/viz/src/components/scene';
import { WebGLTest } from '@graphistry/viz/src/components/webgl-test';
import { Settings, withControlContainer } from '@graphistry/viz/src/containers/settings';
import { LABEL_SETTINGS_SHOW_PROPERTIES_IDX } from '@graphistry/viz/src/models/labels';
import { SCENE_SETTINGS_PRUNE_ORPHANS } from '@graphistry/viz/src/models/scene';

const SceneOptionToggle = withControlContainer(
    ({ id, name, props, value = false, setValue, ...rest }) => (
        <label>
            <input
                type="checkbox"
                checked={value}
                id={`${id}-input`}
                onChange={({ target }) =>
                    setValue({ id, ...rest, value: (value = target.checked) })}
            />
            {name}
        </label>
    )
);



const emptyArray = [];

let Scene = (
    {
        bg = {},
        fg = {},
        settings,
        selectLabel,
        toolbarHeight,
        dataset,
        datasetLoaded,
        sceneShiftDown,
        sceneWheelMove,
        sceneMouseMove,
        sceneTouchStart,
        sceneWidth,
        sceneHeight,
        simulationWidth,
        simulationHeight,
        onSelectedPointTouchStart,
        onSelectionMaskTouchStart,
        id,
        simulating,
        labels = {},
        logo = {},
        page = {},
        axis = {},
        release = {},
        renderer = {},
        scene = {},
        selection = {},
        highlight = {},
        ...props
    } = {}
) => {

    const backgroundColor = props.isKeplerOpen
        ? 'rgba(0, 0, 0, 0)'
        : ((renderer && renderer.background && renderer.background.color) || '#323238');
    
    return (<SceneComponent
        key="scene"
        sceneID={id}
        release={release}
        selection={selection}
        edges={renderer.edges}
        simulating={simulating}
        points={renderer.points}
        onWheel={sceneWheelMove}
        onShiftDown={sceneShiftDown}
        onMouseMove={sceneMouseMove}
        onTouchStart={sceneTouchStart}
        sceneWidth={sceneWidth}
        sceneHeight={sceneHeight}
        simulationWidth={simulationWidth}
        simulationHeight={simulationHeight}
        highlightEnabled={labels && labels.highlightEnabled}
        backgroundColor={backgroundColor}
        isKeplerOpen={props.isKeplerOpen}
        backgroundGradient={bg.gradient}
        backgroundImage={bg.image}
        fgBlendMode={fg.blendMode}
        logoUrl={logo.url}
        pruneIsolatedOption={<SceneOptionToggle data={settings?.[0]?.[SCENE_SETTINGS_PRUNE_ORPHANS]} />}
        labelPOIOption={
            <SceneOptionToggle
                data={labels?.settings?.[0]?.[LABEL_SETTINGS_SHOW_PROPERTIES_IDX]}
            />
        }
        rendererComponent={
            <Renderer
                key="renderer"
                data={renderer}
                simulating={simulating}
                sceneWidth={sceneWidth}
                sceneHeight={sceneHeight}
                datasetLoaded={datasetLoaded}
                simulationWidth={simulationWidth}
                simulationHeight={simulationHeight}
                axis={
                    (axis &&
                        axis.encodings &&
                        axis.encodings.point &&
                        axis.encodings.point.axis &&
                        axis.encodings.point.axis.rows) ||
                    emptyArray
                }
            />
        }
        {...props}
        >
        <WebGLTest />
        <Axis key="axis" data={axis} />
        <Selection
            key="selection"
            data={selection}
            simulating={simulating}
            simulationWidth={simulationWidth}
            simulationHeight={simulationHeight}
            onSelectedPointTouchStart={onSelectedPointTouchStart}
            onSelectionMaskTouchStart={onSelectionMaskTouchStart}
            highlightedEdge={highlight?.edge && highlight.edge[0]}
            highlightedPoint={highlight?.point && highlight.point[0]}
        />
        <Labels
            key="labels"
            data={labels}
            simulating={simulating}
            selectLabel={selectLabel}
            toolbarHeight={toolbarHeight}
            simulationWidth={simulationWidth}
            simulationHeight={simulationHeight}
            sceneSelectionType={selection.type}
        />
        <Logo logoContainer={logo} logoUrl={logo.url} backgroundColor={backgroundColor} dataset={dataset}/>
    </SceneComponent>);
}

Scene = container({
    renderLoading: true,
    fragment: (scene = {}) => `{
        id, simulating,
        release: { tag, buildNumber },
        ... ${Settings.fragment(scene)},
        axis: ${Axis.fragment(scene.axis)},
        renderer: ${Renderer.fragment(scene.renderer)},
        highlight: { point, edge, darken },
        selection: ${Selection.fragment(scene.selection)},
        logo: ${Logo.fragment(scene.logo)},
        fg: { blendMode },
        bg: {
            color,
            image: { blendMode, width, height, url },
            gradient: { kind, position, stops }
        },
        labels: {
            ...${Labels.fragment(scene.labels)},
            settings: {
                0: {
                    2: ${SceneOptionToggle.fragment()}
                }
            }
        },
        page: { favicon, title }
    }`
})(Scene);

export { Scene };
